import { AlertMessage, MUIIcon, ProgressIndicator } from "../../../widgets";
import { ControlType, Property, PropertyType } from "../../../base/types";
import { FC, useCallback, useEffect, useState } from "react";
import { NodeApi, NodeRendererProps, Tree } from "react-arborist";

import { DesignerMode } from "../../types";
import { IControlAttributes } from "../../../base/models";
import { IService } from "./models";
import { Registry } from "../../../base/register";
import { useAuth } from "../../../../modules/auth";
import { useDesignContext } from "../../../providers";
import { useGetProperties } from "../../../base/hooks";
import { useLazyGetContactServicesQuery } from "./services/servicesApi";
import { useNavigate } from "react-router-dom";

export const ServicesComponent: FC<IControlAttributes> = ({
    control, className, children, props, designerMode, ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });
    const hidden: boolean | undefined = properties[Property.Hidden]
    const tooltip: string | undefined = properties[Property.Tooltip];
    const pageId: string | undefined = properties[Property.PageId];

    if (designerMode === DesignerMode.View && hidden === true) {
        return <></>
    }

    return (
        <div className={className} title={tooltip}>
            <Services pageId={pageId} />
        </div>)
}

const Services = ({ pageId }: { pageId?: string }) => {
    const { control: { designerMode } } = useDesignContext()
    const [getServices, { data, isLoading, isSuccess, isError, error }] = useLazyGetContactServicesQuery()
    const [term, setTerm] = useState("");
    const navigate = useNavigate()
    const { siteId, user } = useAuth()

    useEffect(() => {
        const contactId = user?.entityId || user?.contactId;
        if (!contactId) return

        getServices(contactId)
        // eslint-disable-next-line
    }, [user])

    const handleOnSelect = (nodes: NodeApi<IService>[]) => {
        if (designerMode === DesignerMode.Draft || !siteId || !pageId) return;

        const selected = nodes.filter(p => p.isLeaf && p.isSelected).map(p => p.data);
        if (!selected || selected.length === 0)
            return;

        const service = selected[0];
        navigate(`/site(${siteId})/page(${pageId})?serviceId=${service.id}`)
    }

    const getApiErrorMessage = useCallback(() => {
        return (error as any)?.data?.message || "Failed to load services";
    }, [error]);

    return <div>
        <ProgressIndicator show={isLoading} message="Please wait.." />
        <AlertMessage type="Error" show={isError} message={getApiErrorMessage()} />
        {isSuccess && <>
            <div className="input-group input-group-sm mb-2">
                <input type="search"
                    disabled={designerMode === DesignerMode.Draft}
                    placeholder="Search"
                    className="form-control form-control-sm py-2"
                    value={term} onChange={(e) => setTerm(e.target.value)} />
                <span className="input-group-text py-2">
                    <MUIIcon iconName="SearchOutlined" />
                </span>
            </div>

            <Tree initialData={data}
                rowHeight={24}
                paddingBottom={24}
                openByDefault={false}
                width={`100%`}
                searchTerm={term}
                searchMatch={
                    (node, term) => node.data.name.toLowerCase().includes(term.toLowerCase())
                }
                idAccessor={"id"}
                onSelect={handleOnSelect}
                childrenAccessor={"children"}
            >
                {ServiceNode}
            </Tree>
        </>}
    </div>
}

const FolderArrow = ({ node }: { node: NodeApi<IService> }) => {
    if (node.isLeaf) return <span></span>;
    return (
        <span>
            {node.isOpen ?
                <MUIIcon iconName="ArrowDropDownOutlined" /> :
                <MUIIcon iconName="ArrowRightOutlined" />}
        </span>
    );
}

const ServiceNode = ({ node, style }: NodeRendererProps<IService>) => {
    const formatDate = (dateText: Date | string) => {
        return new Date(dateText).toLocaleString(navigator.language, {
            year: "numeric",
            month: "short",
            day: "2-digit"
        });
    }

    return (
        <div
            style={style}
            role='button'

            onClick={() => node.isInternal && node.toggle()}
        >
            {(node.isLeaf) ? <>
                <button
                    type="button"
                    className="btn btn-link btn-sm text-primary fw-semibold py-0">
                    <MUIIcon iconName="ArrowCircleRightOutlined" className="me-1" />
                    {node.data.name}
                </button>
                {node.data.endDate && <>
                    <span className="fs-8 fst-italic text-gray-500 px-2">
                        <span className="me-1">( service will not be available after </span>
                        <span className="fw-bold">{formatDate(node.data.endDate)}</span>
                        <span className="me-1">{' )'}</span>
                    </span>
                </>}
            </> : <>
                <FolderArrow node={node} />
                <span className="fw-semibold">{node.data.name}</span>
            </>}
        </div>
    )
}

Registry.addControl({
    id: "f53e446e-f289-4eec-ae8c-0dd58a1a687c",
    name: "Services",
    type: ControlType.Services,
    properties: [
        { id: Property.Tooltip, name: 'Tooltip', type: PropertyType.String },
        { id: Property.PageId, name: 'Request Page', type: PropertyType.Page },
        { id: Property.Hidden, name: 'Hidden', type: PropertyType.Boolean },
        { id: Property.ProgramType, name: 'Program Type', type: PropertyType.String },
        { id: Property.GenderType, name: 'Gender Type', type: PropertyType.String },
        { id: Property.ServiceType, name: 'Service Type', type: PropertyType.String },
    ],
    component: ServicesComponent
})
